import React, {ReactNode} from 'react'
import {Box, IconButton, useTheme} from '@mui/material'
import {DataGrid, GridColDef, GridRowModel} from '@mui/x-data-grid'
import {FrequencyCode, FrequencyNameMapping} from '../../../domain/Frequency'
import {grey} from '@mui/material/colors'
import {getMonthName} from '../../../utils/formatValuesUtils'
import {AmountWithEuro} from '../../AmountWithEuro'
import DeleteIcon from '@mui/icons-material/Delete'
import {isFieldEditable} from './recurrenceRows'

type RecurrenceDataGridProps = {
  rows: GridRowModel[],
  onProcessRowUpdate: (row: GridRowModel) => GridRowModel,
  onDeleteRecurrence: (id: string) => void,
}

export const RecurrenceDataGrid: React.FC<RecurrenceDataGridProps> = ({rows, onProcessRowUpdate, onDeleteRecurrence}) => {
  const theme = useTheme()
  const monthOptions = Array.from({length: 12}, (_, i) => ({value: i + 1, label: getMonthName(i + 1)}))

  const columns: GridColDef[] = [
    {field: 'description', headerName: 'Description', width: 200, type: 'string', editable: true},
    {field: 'amount', headerName: 'Montant', width: 100, type: 'number', editable: true, renderCell: (p) => <AmountWithEuro amount={p.value}/>},
    {field: 'frequency', headerName: 'Fréquence', width: 120, type: 'singleSelect', valueOptions: Object.values(FrequencyCode).map(v => ({value: v, label: FrequencyNameMapping[v]})), editable: true},
    {field: 'startMonth', headerName: 'Premier mois', width: 120, type: 'singleSelect', valueOptions: monthOptions, editable: true},
    {field: 'startYear', headerName: 'Premier année', width: 120, type: 'number', editable: true},
    {field: 'endMonth', headerName: 'Dernier mois', width: 120, type: 'singleSelect', valueOptions: monthOptions, editable: true},
    {field: 'endYear', headerName: 'Dernière année', width: 120, type: 'number', editable: true},
    {field: 'onMonth', headerName: 'Mois [annuel]', width: 120, type: 'singleSelect', valueOptions: monthOptions, editable: true},
    {
      field: 'delete', headerName: 'Supprimer', width: 100,
      renderCell: (params) => (
        <GridCellIconButtonContainer>
          <IconButton onClick={() => onDeleteRecurrence(params.row.id)} color="recurrence"><DeleteIcon/></IconButton>
        </GridCellIconButtonContainer>
      ),
    },
  ]

  return (
    <Box style={{height: 400, width: '100%'}}>
      <DataGrid
        rows={rows}
        columns={columns}
        localeText={{noRowsLabel: 'Pas de récurrence'}}
        hideFooter
        processRowUpdate={onProcessRowUpdate}
        isCellEditable={isCellEditable}
        getCellClassName={getCellBackground}
        sx={{
          '& .MuiDataGrid-withBorderColor': {backgroundColor: theme.palette.recurrence.light, color: theme.palette.common.black},
          '& .disabled-cell': {backgroundColor: grey[100]},
        }}
      />
    </Box>
  )
}

const isCellEditable = (params: any) => isFieldEditable(params.row.frequency, params.field)
const getCellBackground = (params: any) => (isCellEditable(params) ? '' : 'disabled-cell')

const GridCellIconButtonContainer: React.FC<{children: ReactNode}> = ({children}) => (
  <Box display="flex" justifyContent="center" alignItems="center" sx={{height: '100%'}}>{children}</Box>
)
